/**
 * The one place a price changes units.
 *
 * The venue quotes **yen/MWh** as a scaled integer — shifted by `price_delimiter`
 * decimal places, in practice 10000, so hundredths of a cent. The desk reads and
 * types **yen/kWh**. Every conversion between those lives here, because the same
 * arithmetic used to be spelled four different ways across the app and two of them
 * disagreed about the scale.
 *
 *     wire integer  --/ 10^delimiter -->  yen/MWh  --/ 1000 -->  yen/kWh   (display)
 *     yen/kWh       --* 1000 ------------------->  yen/MWh                 (outbound)
 */

/**
 * Fallback when a payload carries no `price_delimiter` — the REST depth snapshots
 * carry no scale field at all.
 *
 * Price precision is a property of the venue rather than of a feed or a contract,
 * so there is one delimiter for JEPX and both NATS feeds report it. That replaces
 * the old split where the quotes feed defaulted to 2 and trades and depth to 4,
 * which had the same venue scaled two different ways depending on which grid you
 * were looking at.
 */
export const DEFAULT_PRICE_DELIMITER = 4;

const KWH_PER_MWH = 1000;

/**
 * Guards float noise. `1.0800000000000001` sitting beside the order book's `1.08`
 * reads as a different price, and the two are compared for the depth ladder's
 * highlight. Applied on construction and again on the kWh derivation, so every
 * path that reaches the same price reaches the same double — which is what lets
 * {@link Price.equals} compare exactly.
 */
const normalise = (value: number): number => Number(value.toFixed(6));

export class Price {
  /** Held in yen/MWh: the unit the backend speaks. */
  private constructor(private readonly _yenPerMwh: number) {}

  /**
   * A scaled integer as the venue sends it — yen/MWh shifted `delimiter` places.
   * Covers the NATS quotes and private trades, the REST depth snapshots, and
   * `algo_data.price` off lazerhub.
   */
  static fromScaledMwh(
    raw: number,
    delimiter: number = DEFAULT_PRICE_DELIMITER
  ): Price {
    return new Price(normalise(raw / 10 ** delimiter));
  }

  /**
   * An already-decimal yen/MWh figure: `configuration.price` on an algo, which the
   * hub echoes back in the same unit we send it.
   */
  static fromYenPerMwh(value: number): Price {
    return new Price(normalise(value));
  }

  /**
   * An already-decimal yen/kWh figure. Only the .NET spot-price provider, which is
   * a different backend and did not change unit.
   */
  static fromYenPerKwh(value: number): Price {
    return new Price(normalise(value * KWH_PER_MWH));
  }

  /**
   * Trader input, in yen/kWh. Takes the comma-or-dot string formik actually holds
   * — `ConfigTabState.price` is typed as a number but is a string at runtime — and
   * returns undefined for blank or unparseable rather than a price of zero.
   */
  static fromInput(
    value: string | number | null | undefined
  ): Price | undefined {
    if (value === null || value === undefined || value === "") {
      return undefined;
    }

    const parsed =
      typeof value === "number"
        ? value
        : Number(String(value).replace(",", "."));

    return Number.isFinite(parsed) ? Price.fromYenPerKwh(parsed) : undefined;
  }

  /** The outbound wire value. */
  get yenPerMwh(): number {
    return this._yenPerMwh;
  }

  /** Everything the desk sees. */
  get yenPerKwh(): number {
    return normalise(this._yenPerMwh / KWH_PER_MWH);
  }

  /**
   * Value equality. `===` on a class is reference equality and would be silently
   * false for two prices that are the same number, so callers must use this.
   */
  equals(other: Price | undefined | null): boolean {
    return (
      other !== null &&
      other !== undefined &&
      this._yenPerMwh === other._yenPerMwh
    );
  }
}

const delimiterBySource = new Map<string, number>();
let delimiterConflictReported = false;

/**
 * Guards the assumption this module is built on: price precision is a property of
 * the venue, so every feed reports the same `price_delimiter`. That is what lets
 * the REST depth snapshots — which carry no scale of their own — borrow one from
 * the NATS quotes.
 *
 * If it ever stops holding, one grid starts quietly mispricing while the others
 * look right, which is close to undebuggable from a screenshot. So say it out loud
 * once, rather than discovering it from a fill.
 */
export const recordPriceDelimiter = (
  source: string,
  delimiter: number | undefined
): void => {
  if (delimiter === undefined || delimiterConflictReported) {
    return;
  }

  delimiterBySource.set(source, delimiter);

  if (new Set(delimiterBySource.values()).size > 1) {
    delimiterConflictReported = true;
    console.warn(
      "Feeds disagree on price_delimiter — depth prices borrow the quote feed's " +
        "scale and will be wrong for at least one grid:",
      Object.fromEntries(delimiterBySource)
    );
  }
};

/** Display string in yen/kWh, with the `-` the grids already use for absent. */
export const formatYenPerKwh = (price: Price | undefined | null): string =>
  price === undefined || price === null ? "-" : String(price.yenPerKwh);

/** Suffix for column headers and the order-entry field label. */
export const PRICE_UNIT = "¥/kWh";
